import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Check, Eye, EyeOff, Minus } from 'lucide-react';
import axios from '../../../axios';
import { openIdentityModal, openMainModal } from '../../../store/actions';
import { setStartKycAuthIntent } from '../../../utils/authIntent.ts';
import {
  validatePassword,
  validateConfirmPassword,
} from '../../../utils/registration/registrationHelpers.ts';
import { PASSWORD_VALIDATION } from '../../../utils/registration/registrationConstants.ts';
import styles from './AccountSecuritySection.module.scss';

function parseApiErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string | string[] } } };
  const raw = e?.response?.data?.message;
  if (Array.isArray(raw) && raw.length) return raw.join(', ');
  if (typeof raw === 'string') return raw;
  return fallback;
}

type RuleRowProps = {
  met: boolean;
  label: string;
};

const RuleRow: React.FC<RuleRowProps> = ({ met, label }) => (
  <li
    className={`${styles.ruleRow} ${met ? styles.ruleMet : styles.ruleUnmet}`}
    aria-label={met ? `${label}: cumplido` : `${label}: pendiente`}
  >
    <span className={styles.ruleIcon} aria-hidden>
      {met ? <Check size={16} strokeWidth={2.5} /> : <Minus size={16} strokeWidth={2} />}
    </span>
    <span>{label}</span>
  </li>
);

const AccountSecuritySection: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector((state: any) => state.general?.userData);
  const userEmail = (
    userData?.email ||
    userData?.username ||
    localStorage.getItem('EmailRegister') ||
    ''
  )
    .trim()
    .toLowerCase();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [currentError, setCurrentError] = useState('');
  const [newError, setNewError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    minLen,
    hasLetter,
    hasNumber,
    passwordsMatch,
    distinctFromCurrent,
    distinctRuleMet,
    currentFilled,
  } = useMemo(() => {
    const minLen = newPassword.length >= PASSWORD_VALIDATION.MIN_LENGTH;
    const hasLetter = PASSWORD_VALIDATION.REQUIRE_LETTER
      ? /[a-zA-Z]/.test(newPassword)
      : true;
    const hasNumber = PASSWORD_VALIDATION.REQUIRE_NUMBER ? /\d/.test(newPassword) : true;
    const passwordsMatch =
      confirmPassword.length > 0 && newPassword === confirmPassword;
    const currentFilled = currentPassword.trim().length > 0;
    const distinctFromCurrent =
      newPassword.length === 0 ||
      currentPassword.length === 0 ||
      newPassword !== currentPassword;
    const distinctRuleMet =
      currentPassword.length > 0 &&
      newPassword.length > 0 &&
      newPassword !== currentPassword;
    return {
      minLen,
      hasLetter,
      hasNumber,
      passwordsMatch,
      distinctFromCurrent,
      distinctRuleMet,
      currentFilled,
    };
  }, [currentPassword, newPassword, confirmPassword]);

  const newPasswordSchemaOk = useMemo(() => {
    const r = validatePassword(newPassword);
    return r.isValid;
  }, [newPassword]);

  const confirmOk = useMemo(() => {
    const r = validateConfirmPassword(newPassword, confirmPassword);
    return r.isValid;
  }, [newPassword, confirmPassword]);

  const canSubmit =
    currentFilled &&
    newPasswordSchemaOk &&
    confirmOk &&
    distinctFromCurrent &&
    !loading;

  const identityValidationStatus =
    userData?.jobBoard?.identity_validation_status || userData?.identity_validation_status;
  const identityStatus: 'validated' | 'pending' | 'rejected' | 'not_started' =
    identityValidationStatus === 'APPROVED'
      ? 'validated'
      : identityValidationStatus === 'PENDING_REVIEW'
        ? 'pending'
        : identityValidationStatus === 'REJECTED'
          ? 'rejected'
          : 'not_started';

  const resetFieldErrors = () => {
    setCurrentError('');
    setNewError('');
    setConfirmError('');
    setApiError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    resetFieldErrors();

    if (!currentPassword.trim()) {
      setCurrentError('Ingresa tu contraseña actual');
      return;
    }

    const newPwValidation = validatePassword(newPassword);
    if (!newPwValidation.isValid) {
      setNewError(newPwValidation.error || '');
      return;
    }

    const confirmValidation = validateConfirmPassword(newPassword, confirmPassword);
    if (!confirmValidation.isValid) {
      setConfirmError(confirmValidation.error || '');
      return;
    }

    if (newPassword === currentPassword) {
      setNewError('La nueva contraseña debe ser distinta a la actual');
      return;
    }

    const token = localStorage.getItem('access-Token-Edify');
    if (!token) {
      setApiError('No hay sesión activa. Inicia sesión nuevamente.');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.patch<{ message?: string }>(
        '/user/data/password',
        { currentPassword, newPassword },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
      const successBody =
        typeof res.data?.message === 'string' && res.data.message.trim()
          ? res.data.message
          : 'Tu contraseña se cambió correctamente.';
      dispatch(
        openMainModal({
          title: 'Contraseña actualizada',
          body: successBody,
          isSuccess: true,
          confirmHandler: null,
          iconType: 'success',
        }) as any
      );
    } catch (err: unknown) {
      setApiError(
        parseApiErrorMessage(
          err,
          'No pudimos cambiar la contraseña. Intenta de nuevo en unos minutos.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const submitClassName = [
    styles.submitButton,
    loading ? styles.submitButtonLoading : '',
    !canSubmit && !loading ? styles.submitButtonInactive : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleStartIdentityValidation = () => {
    const token = localStorage.getItem('access-Token-Edify');

    if (!token) {
      const profileId = userData?.id ? `/perfil/${userData.id}` : '/dashboard';
      setStartKycAuthIntent({
        source: 'profile',
        returnTo: `${profileId}?openAccountSettings=1`,
        email: userEmail || undefined,
      });
      navigate('/');
      return;
    }

    dispatch(
      openIdentityModal({
        isOpen: true,
        email: userEmail,
        source: 'profile',
      }) as any
    );
  };

  return (
    <section className={styles.section} aria-labelledby="account-security-heading">
      <h4 id="account-security-heading" className={styles.title}>
        Seguridad
      </h4>
      <p className={styles.subtitle}>
        Cambia la contraseña de tu cuenta. Si iniciaste sesión solo con Google u otro proveedor y no
        tienes contraseña en Edify, usa la recuperación desde el inicio de sesión.
      </p>
      <div className={styles.identityRow}>
        <p className={styles.identityCopy}>
          También puedes iniciar la validación de identidad para completar la seguridad de tu cuenta.
        </p>
        <div className={styles.identityStatusRow}>
          <span
            className={`${styles.identityBadge} ${
              identityStatus === 'validated'
                ? styles.identityBadgeValidated
                : identityStatus === 'pending'
                  ? styles.identityBadgePending
                  : identityStatus === 'rejected'
                    ? styles.identityBadgeRejected
                  : styles.identityBadgeNotStarted
            }`}
          >
            {identityStatus === 'validated'
              ? 'Cuenta validada'
              : identityStatus === 'pending'
                ? 'En revisión'
                : identityStatus === 'rejected'
                  ? 'Rechazada'
                : 'No iniciada'}
          </span>
          {(identityStatus === 'not_started' || identityStatus === 'rejected') ? (
            <button
              type="button"
              className={styles.identityButton}
              onClick={handleStartIdentityValidation}
              disabled={loading}
            >
              {identityStatus === 'rejected' ? 'Reenviar documentación' : 'Validar identidad'}
            </button>
          ) : null}
        </div>
      </div>
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <div className={styles.field}>
          <label htmlFor="current-password-account" className={styles.label}>
            Contraseña actual
          </label>
          <div className={styles.inputWrap}>
            <input
              id="current-password-account"
              className={`${styles.input} ${styles.inputWithToggle}`}
              type={showCurrent ? 'text' : 'password'}
              autoComplete="current-password"
              value={currentPassword}
              onChange={(ev) => {
                setCurrentPassword(ev.target.value);
                if (currentError) setCurrentError('');
                if (apiError) setApiError('');
              }}
              disabled={loading}
            />
            <button
              type="button"
              className={styles.visibilityToggle}
              onClick={() => setShowCurrent((v) => !v)}
              disabled={loading}
              aria-label={showCurrent ? 'Ocultar contraseña actual' : 'Mostrar contraseña actual'}
              aria-pressed={showCurrent}
            >
              {showCurrent ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {currentError ? <p className={styles.fieldError}>{currentError}</p> : null}
        </div>
        <div className={styles.field}>
          <label htmlFor="new-password-account" className={styles.label}>
            Nueva contraseña
          </label>
          <div className={styles.inputWrap}>
            <input
              id="new-password-account"
              className={`${styles.input} ${styles.inputWithToggle}`}
              type={showNew ? 'text' : 'password'}
              autoComplete="new-password"
              value={newPassword}
              onChange={(ev) => {
                setNewPassword(ev.target.value);
                if (newError) setNewError('');
                if (apiError) setApiError('');
              }}
              disabled={loading}
            />
            <button
              type="button"
              className={styles.visibilityToggle}
              onClick={() => setShowNew((v) => !v)}
              disabled={loading}
              aria-label={showNew ? 'Ocultar nueva contraseña' : 'Mostrar nueva contraseña'}
              aria-pressed={showNew}
            >
              {showNew ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {newError ? <p className={styles.fieldError}>{newError}</p> : null}
          <ul className={styles.rulesList} aria-live="polite">
            <RuleRow
              met={minLen}
              label={`Mínimo ${PASSWORD_VALIDATION.MIN_LENGTH} caracteres`}
            />
            {PASSWORD_VALIDATION.REQUIRE_LETTER ? (
              <RuleRow met={hasLetter} label="Al menos una letra" />
            ) : null}
            {PASSWORD_VALIDATION.REQUIRE_NUMBER ? (
              <RuleRow met={hasNumber} label="Al menos un número" />
            ) : null}
            <RuleRow met={distinctRuleMet} label="Distinta a la contraseña actual" />
          </ul>
        </div>
        <div className={styles.field}>
          <label htmlFor="confirm-password-account" className={styles.label}>
            Confirmar nueva contraseña
          </label>
          <div className={styles.inputWrap}>
            <input
              id="confirm-password-account"
              className={`${styles.input} ${styles.inputWithToggle}`}
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(ev) => {
                setConfirmPassword(ev.target.value);
                if (confirmError) setConfirmError('');
                if (apiError) setApiError('');
              }}
              disabled={loading}
            />
            <button
              type="button"
              className={styles.visibilityToggle}
              onClick={() => setShowConfirm((v) => !v)}
              disabled={loading}
              aria-label={
                showConfirm ? 'Ocultar confirmación de contraseña' : 'Mostrar confirmación de contraseña'
              }
              aria-pressed={showConfirm}
            >
              {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {confirmError ? <p className={styles.fieldError}>{confirmError}</p> : null}
          <ul className={styles.rulesList} aria-live="polite">
            <RuleRow met={passwordsMatch} label="Las contraseñas nuevas coinciden" />
          </ul>
        </div>
        {apiError ? (
          <p className={styles.apiError} role="alert">
            {apiError}
          </p>
        ) : null}
        <div className={styles.submitRow}>
          <button type="submit" className={submitClassName} disabled={!canSubmit}>
            {loading ? (
              <>
                <span className={styles.spinner} aria-hidden />
                Guardando…
              </>
            ) : (
              'Cambiar contraseña'
            )}
          </button>
        </div>
      </form>
    </section>
  );
};

export default AccountSecuritySection;
